import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { Lock, Eye, EyeOff, Check, X, Shield, Sparkles } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Composant pour afficher un item de validation
const ValidationItem = ({ valid, text }) => (
  <div className={`flex items-center gap-2 text-sm ${valid ? 'text-emerald-600' : 'text-slate-400'}`}>
    {valid ? (
      <Check className="h-4 w-4 text-emerald-500" />
    ) : (
      <X className="h-4 w-4 text-slate-300" />
    )}
    <span>{text}</span>
  </div>
);

/**
 * Composant pour changer le mot de passe
 * Utilisable dans tous les espaces (Super Admin, Admin, COOPER'S, Membre)
 */
export default function ChangePasswordForm({ getAuthHeader, onSuccess }) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  // Validation du mot de passe
  const passwordValidation = {
    minLength: formData.newPassword.length >= 8,
    hasUppercase: /[A-Z]/.test(formData.newPassword),
    hasLowercase: /[a-z]/.test(formData.newPassword),
    hasNumber: /[0-9]/.test(formData.newPassword),
    hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword),
    passwordsMatch: formData.newPassword === formData.confirmPassword && formData.confirmPassword !== ''
  };

  const isPasswordValid = passwordValidation.minLength && 
    passwordValidation.hasUppercase && 
    passwordValidation.hasLowercase && 
    passwordValidation.hasNumber;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Le mot de passe ne respecte pas les critères de sécurité');
      return;
    }

    if (!passwordValidation.passwordsMatch) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      await axios.put(
        `${BACKEND_URL}/api/auth/change-password`,
        {
          current_password: formData.currentPassword,
          new_password: formData.newPassword
        },
        { headers: getAuthHeader() }
      );

      toast.success('Mot de passe modifié avec succès !');
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setIsOpen(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) {
    return (
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Lock className="h-5 w-5 text-slate-600" />
            Mot de passe
          </CardTitle>
          <CardDescription>Modifiez votre mot de passe de connexion</CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => setIsOpen(true)}
          >
            <Lock className="h-4 w-4 mr-2" />
            Changer le mot de passe
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200 border-2 border-amber-200 bg-amber-50/30">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Shield className="h-5 w-5 text-amber-600" />
          Modifier le mot de passe
        </CardTitle>
        <CardDescription>
          Entrez votre mot de passe actuel puis définissez un nouveau mot de passe sécurisé
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Mot de passe actuel */}
          <div className="space-y-2">
            <Label htmlFor="currentPassword" className="text-sm font-medium">
              Mot de passe actuel
            </Label>
            <div className="relative">
              <Input
                id="currentPassword"
                name="currentPassword"
                type={showPasswords.current ? 'text' : 'password'}
                value={formData.currentPassword}
                onChange={handleChange}
                className="pr-10"
                placeholder="Entrez votre mot de passe actuel"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
              >
                {showPasswords.current ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Nouveau mot de passe */}
          <div className="space-y-2">
            <Label htmlFor="newPassword" className="text-sm font-medium">
              Nouveau mot de passe
            </Label>
            <div className="relative">
              <Input
                id="newPassword"
                name="newPassword"
                type={showPasswords.new ? 'text' : 'password'}
                value={formData.newPassword}
                onChange={handleChange}
                className="pr-10"
                placeholder="Entrez votre nouveau mot de passe"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
              >
                {showPasswords.new ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Critères de validation */}
          {formData.newPassword && (
            <div className="bg-slate-50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium text-slate-700 mb-2">Critères de sécurité :</p>
              <ValidationItem valid={passwordValidation.minLength} text="Au moins 8 caractères" />
              <ValidationItem valid={passwordValidation.hasUppercase} text="Une lettre majuscule" />
              <ValidationItem valid={passwordValidation.hasLowercase} text="Une lettre minuscule" />
              <ValidationItem valid={passwordValidation.hasNumber} text="Un chiffre" />
              <ValidationItem valid={passwordValidation.hasSpecial} text="Un caractère spécial (optionnel)" />
            </div>
          )}

          {/* Confirmation du mot de passe */}
          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-sm font-medium">
              Confirmer le nouveau mot de passe
            </Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type={showPasswords.confirm ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`pr-10 ${
                  formData.confirmPassword && 
                  (passwordValidation.passwordsMatch ? 'border-emerald-500' : 'border-red-500')
                }`}
                placeholder="Confirmez votre nouveau mot de passe"
                required
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
              >
                {showPasswords.confirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {formData.confirmPassword && !passwordValidation.passwordsMatch && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <X className="h-4 w-4" />
                Les mots de passe ne correspondent pas
              </p>
            )}
            {passwordValidation.passwordsMatch && (
              <p className="text-sm text-emerald-500 flex items-center gap-1">
                <Check className="h-4 w-4" />
                Les mots de passe correspondent
              </p>
            )}
          </div>

          {/* Boutons */}
          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => {
                setIsOpen(false);
                setFormData({ currentPassword: '', newPassword: '', confirmPassword: '' });
              }}
            >
              Annuler
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400"
              disabled={loading || !isPasswordValid || !passwordValidation.passwordsMatch}
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Modification...
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4" />
                  Modifier le mot de passe
                </div>
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
